// src/hooks/useTokenManager.js - Token management via backend smart refresh
import { useState, useCallback, useEffect } from 'react';
import logger from '../services/logger';
import TokenStorage from '../services/TokenStorage';

export const useTokenManager = () => {
  const [tokens, setTokens] = useState({
    spotify: { token: null, expiry: null, refreshing: false },
    youtube: { token: null, expiry: null, refreshing: false }
  });

  // Initialize tokens from Supabase on mount (primary source of truth)
  // Falls back to localStorage only if Supabase fetch fails
  useEffect(() => {
    const initializeFromSupabase = async () => {
      try {
        logger.progress('Initializing token manager from Supabase...');

        const platformTokens = await TokenStorage.getPlatformTokens();

        if (platformTokens && platformTokens.accessToken) {
          const platform = platformTokens.platform;
          const expiry = platformTokens.expiry ? new Date(platformTokens.expiry).getTime() : Date.now() + 3600000;

          setTokens(prev => ({
            ...prev,
            [platform]: {
              token: platformTokens.accessToken,
              expiry: expiry,
              refreshing: false
            }
          }));

          // Cache to localStorage for offline fallback
          if (platform === 'spotify') {
            TokenStorage.setSpotifyTokens(platformTokens.accessToken, platformTokens.refreshToken, 3600);
          } else if (platform === 'youtube') {
            TokenStorage.setYouTubeTokens(platformTokens.accessToken, platformTokens.refreshToken, 3600);
          }

          logger.progress(`Token manager initialized from Supabase (${platform})`);
        } else {
          logger.warn('No tokens in Supabase, falling back to localStorage cache');
          initializeFromLocalStorageFallback();
        }
      } catch (error) {
        logger.warn('Failed to fetch from Supabase, using localStorage fallback:', error);
        initializeFromLocalStorageFallback();
      }
    };

    const initializeFromLocalStorageFallback = () => {
      const spotifyTokens = TokenStorage.getSpotifyTokens();
      const youtubeTokens = TokenStorage.getYouTubeTokens();

      setTokens({
        spotify: {
          token: spotifyTokens.accessToken,
          expiry: spotifyTokens.expiry,
          refreshing: false
        },
        youtube: {
          token: youtubeTokens.accessToken,
          expiry: youtubeTokens.expiry,
          refreshing: false
        }
      });

      logger.progress('Token manager initialized from localStorage (fallback)');
    };

    initializeFromSupabase();
  }, []);

  // Get valid token — backend handles expiry check and refresh automatically
  const getValidToken = useCallback(async (platform) => {
    try {
      const platformTokens = await TokenStorage.getPlatformTokens();

      if (platformTokens && platformTokens.platform === platform && platformTokens.accessToken) {
        setTokens(prev => ({
          ...prev,
          [platform]: {
            token: platformTokens.accessToken,
            expiry: platformTokens.expiry,
            refreshing: false
          }
        }));

        // Keep localStorage in sync
        if (platform === 'spotify') {
          TokenStorage.setSpotifyTokens(platformTokens.accessToken, platformTokens.refreshToken, 3600);
        } else if (platform === 'youtube') {
          TokenStorage.setYouTubeTokens(platformTokens.accessToken, platformTokens.refreshToken, 3600);
        }

        return platformTokens.accessToken;
      }

      // Fallback to localStorage
      logger.warn(`Backend fetch returned no ${platform} token, trying localStorage`);
      const storedTokens = platform === 'spotify'
        ? TokenStorage.getSpotifyTokens()
        : TokenStorage.getYouTubeTokens();

      if (storedTokens.accessToken) {
        return storedTokens.accessToken;
      }

      logger.warn(`No valid ${platform} token found`);
      return null;
    } catch (error) {
      logger.error(`Error getting ${platform} token:`, error);

      const storedTokens = platform === 'spotify'
        ? TokenStorage.getSpotifyTokens()
        : TokenStorage.getYouTubeTokens();

      if (storedTokens.accessToken) {
        return storedTokens.accessToken;
      }

      return null;
    }
  }, []);

  // Store tokens after OAuth success
  const storeTokens = useCallback((platform, accessToken, refreshToken, expiresIn = 3600) => {
    logger.debug(`Storing ${platform} tokens`);

    let expiry;
    if (platform === 'spotify') {
      expiry = TokenStorage.setSpotifyTokens(accessToken, refreshToken, expiresIn);
    } else if (platform === 'youtube') {
      expiry = TokenStorage.setYouTubeTokens(accessToken, refreshToken, expiresIn);
    }

    setTokens(prev => ({
      ...prev,
      [platform]: {
        token: accessToken,
        expiry,
        refreshing: false
      }
    }));

    return expiry;
  }, []);

  // Manual refresh — delegates to getValidToken (backend handles actual refresh)
  const manualRefresh = useCallback(async (platform) => {
    return await getValidToken(platform);
  }, [getValidToken]);

  // Clear tokens
  const clearTokens = useCallback((platform = null) => {
    if (platform) {
      if (platform === 'spotify') {
        TokenStorage.clearSpotifyTokens();
      } else if (platform === 'youtube') {
        TokenStorage.clearYouTubeTokens();
      }
      setTokens(prev => ({
        ...prev,
        [platform]: { token: null, expiry: null, refreshing: false }
      }));
      logger.info(`Cleared ${platform} tokens`);
    } else {
      TokenStorage.clearSpotifyTokens();
      TokenStorage.clearYouTubeTokens();
      setTokens({
        spotify: { token: null, expiry: null, refreshing: false },
        youtube: { token: null, expiry: null, refreshing: false }
      });
      logger.info('Cleared all tokens');
    }
  }, []);

  const needsReauth = useCallback((platform) => {
    const storedTokens = platform === 'spotify'
      ? TokenStorage.getSpotifyTokens()
      : TokenStorage.getYouTubeTokens();
    return !storedTokens.accessToken;
  }, []);

  const getTokenInfo = useCallback((platform) => {
    const stored = platform === 'spotify'
      ? TokenStorage.getSpotifyTokens()
      : TokenStorage.getYouTubeTokens();
    const state = tokens[platform];
    return {
      hasStoredAccess: !!stored.accessToken,
      hasStoredRefresh: !!stored.refreshToken,
      storedExpiry: stored.expiry,
      stateToken: !!state.token,
      stateExpiry: state.expiry,
      isRefreshing: state.refreshing,
      timeUntilExpiry: stored.expiry ? stored.expiry - Date.now() : null
    };
  }, [tokens]);

  return {
    tokens,
    getValidToken,
    storeTokens,
    clearTokens,
    needsReauth,
    manualRefresh,
    getTokenInfo,

    // Legacy exports — all delegate to getValidToken (backend handles refresh)
    refreshSpotifyTokenDirect: () => getValidToken('spotify'),
    refreshYouTubeTokenDirect: () => getValidToken('youtube'),
    refreshSpotifyToken: () => getValidToken('spotify'),
    refreshYouTubeToken: () => getValidToken('youtube'),
    initializeTokens: async () => logger.info('Token manager initialized')
  };
};
